import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { getFocusElement } from "@swa-ui/browser";
import { classNames, getUniqueId } from "@swa-ui/string";

import { Area } from "../Area";
import globalRules from "../assets/styles/globalRules.module.scss";
import { Button } from "../Button";
import { keyCodes } from "../defines/keyCodes";
import { HorizontalGestureScroller } from "../HorizontalGestureScroller";
import { Transition } from "../Transition";
import { TransitionBlock } from "../TransitionBlock";
import styles from "./Tabs.module.scss";

const CONTENT_TRANSITION_IN = { delay: 250, duration: 200, transformations: ["fadeReset"] };
const CONTENT_TRANSITION_OUT = { duration: 100, transformations: ["fade"] };
const NEXT_TAB_DIRECTION = 1;
const PREVIOUS_TAB_DIRECTION = -1;
const TRANSITION_DURATION = 0.75;

/**
 * Tabs renders a list of tabs to navigate, which when selected, will update content panel below
 * buttons.
 */
export const Tabs = (props) => {
  const {
    animateContent = true,
    animateHeight = true,
    animateInitialRender = true,
    animationToken,
    autoFit = false,
    className,
    contentClassName,
    defaultSelectedIndex,
    disabled = false,
    fullWidth = false,
    items,
    onChange,
    onKeyDown,
    padding = "normal",
    paddingContent = "normal",
    selectedIndex,
    showBackground = true,
    onTransitionEnd,
  } = props;
  const [animate, setAnimate] = useState();
  const [currentSelectedIndex, setCurrentSelectedIndex] = useState(getInitialSelectedIndex());
  const [focusedTabIndex, setFocusedTabIndex] = useState(-1);
  const [initialized, setInitialized] = useState(false);
  const [isFullWidth, setIsFullWidth] = useState(fullWidth);
  const [isSelectedUpdatedInternally, setIsSelectedUpdatedInternally] = useState(false);
  const [renderedFirstTime, setRenderedFirstTime] = useState(false);
  const [previousActiveElement, setPreviousActiveElement] = useState();
  const [scrollPosition, setScrollPosition] = useState(0);
  const tabsRef = useRef();
  const tabsId = useMemo(() => getUniqueId("tabs"), []);

  useEffect(() => {
    setScrollPosition(ensureButtonIsFullyVisible());
  }, [currentSelectedIndex, focusedTabIndex]);

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    selectedIndex !== undefined && setCurrentSelectedIndex(selectedIndex);
  }, [selectedIndex]);

  useEffect(() => {
    if (initialized && !isSelectedUpdatedInternally) {
      setPreviousActiveElement(getFocusElement());
      document.getElementById(getTabId(selectedIndex)).focus();
    }

    setIsSelectedUpdatedInternally(false);
  }, [selectedIndex]);

  useEffect(() => {
    const currentActiveElement = getFocusElement();
    const isFocusedOnTabs = tabsRef.current.contains(currentActiveElement.parentElement);

    if (isFocusedOnTabs) {
      document.getElementById(getTabId(currentSelectedIndex)).focus();
    }
  }, [currentSelectedIndex]);

  useEffect(() => {
    if (autoFit && items?.length) {
      const lastTab = document.getElementById(getTabId(items.length - 1));
      const boundRect = lastTab.getBoundingClientRect();
      const widthOfAllTabs = boundRect.x + boundRect.width;

      setIsFullWidth(widthOfAllTabs < window.innerWidth);
    }
  }, [window.innerWidth]);

  return (
    <div onFocus={handleFocus}>
      <Transition {...getTransitionProps()}>
        {renderTabs()}
        <div className={getClass()}>
          <Area {...getAreaProps()}>
            <div {...getContentContainerProps()}>{renderContent()}</div>
          </Area>
        </div>
      </Transition>
    </div>
  );

  function renderTabs() {
    const content = items.map(renderTab);

    return isFullWidth ? (
      <div {...getProps()}>{content}</div>
    ) : (
      <HorizontalGestureScroller {...getProps()} {...getHorizontalGestureScrollerProps()}>
        {content}
      </HorizontalGestureScroller>
    );
  }

  function renderTab(item, index) {
    const { label } = item;

    return (
      <Button {...getTabProps(index)} style={getWidthStyle()}>
        <div className={styles.labelContainer}>
          <div className={getHiddenLabelClass()}>{label}</div>
          <div className={styles.onTopAndVisible}>{label}</div>
        </div>
        <div className={getHoverIndicatorClass(index)} />
      </Button>
    );
  }

  function renderContent() {
    const { content } = items[currentSelectedIndex];
    const contentToBeRendered = typeof content === "function" ? content() : content;

    return animateContent ? (
      <TransitionBlock {...getTransitionBlockProps()}>{contentToBeRendered}</TransitionBlock>
    ) : (
      contentToBeRendered
    );
  }

  function getProps() {
    return {
      className: classNames(styles.tabsContainer, className, {
        [styles.fullWidthContainer]: isFullWidth,
      }),
      onClick: () => setIsSelectedUpdatedInternally(true),
      onKeyDown: (event) => {
        onKeyDown?.(event);
        handleKeyDown(event);
      },
      ref: tabsRef,
      role: "tablist",
      ...(!isFullWidth && {
        scrollPosition,
        tabIndex: -1,
      }),
    };
  }

  function getHorizontalGestureScrollerProps() {
    return {
      "aria-label-next": props["aria-label-next"],
      "aria-label-previous": props["aria-label-previous"],
    };
  }

  function getContentContainerProps() {
    return {
      "aria-labelledby": getTabId(currentSelectedIndex),
      className: contentClassName,
      id: getContentId(currentSelectedIndex),
      role: "tabpanel",
    };
  }

  function getTabProps(index) {
    const { "aria-label": ariaLabel, key, label } = items[index];
    const isSelected = currentSelectedIndex === index;

    return {
      "aria-controls": isSelected ? getContentId(index) : undefined,
      "aria-label": ariaLabel,
      "aria-selected": isSelected,
      className: getTabClass(index, isSelected),
      disabled: disabled || items[index]?.disabled,
      id: getTabId(index),
      key: key ?? (typeof label === "string" ? label : index),
      onClick: (event) => handleClick(event, index),
      role: "tab",
      showUnderline: false,
      styleType: "no-style",
      tabIndex: isSelected ? undefined : -1,
    };
  }

  function getTransitionBlockProps() {
    return {
      animationToken: animationToken ?? currentSelectedIndex,
      transitionIn: CONTENT_TRANSITION_IN,
      transitionOut: CONTENT_TRANSITION_OUT,
    };
  }

  function getTransitionProps() {
    return {
      animate: { opacity: renderedFirstTime || !animateInitialRender ? 1 : 0 },
      initial: { opacity: animateInitialRender ? 0 : 1 },
      transition: {
        duration: !renderedFirstTime || !animateInitialRender ? 0 : TRANSITION_DURATION,
      },
    };
  }

  function getAreaProps() {
    return {
      animate: animateHeight && animate,
      onTransitionEnd: handleTransitionEnd,
      revealed: true,
    };
  }

  function getClass() {
    return classNames(styles.content, {
      [styles.contentBackground]: showBackground,
      [styles.paddingNone]: paddingContent === "none",
    });
  }

  function getHiddenLabelClass() {
    return classNames(styles.hidden, styles.emphasis, {
      [styles.paddingNone]: padding === "none" || isFullWidth,
      [styles.paddingSmall]: padding === "small",
    });
  }

  function getHoverIndicatorClass(index) {
    return classNames(styles.hoverIndicator, {
      [styles.active]: currentSelectedIndex === index,
      [styles.disabled]: disabled || items[index]?.disabled,
    });
  }

  function getTabClass(tabIndex, isSelected) {
    return classNames(styles.tab, globalRules.fontSize16, {
      [styles.emphasis]: isSelected,
      [styles.disabled]: disabled || items[tabIndex]?.disabled,
    });
  }

  function handleFocus(event) {
    if (previousActiveElement && !isSelectedUpdatedInternally) {
      previousActiveElement.focus();
      setPreviousActiveElement(undefined);
    }
  }

  function handleClick(event, index) {
    updateSelectedIndex(index);
  }

  function handleTransitionEnd() {
    setAnimate(false);
    setRenderedFirstTime(true);
    onTransitionEnd?.();
  }

  function handleKeyDown(event) {
    const { key } = event;
    let newSelectedIndex = currentSelectedIndex;

    setIsSelectedUpdatedInternally(true);

    if (!event.defaultPrevented) {
      if (key === keyCodes.KEY_RIGHT) {
        newSelectedIndex = getNextSelectableIndex(NEXT_TAB_DIRECTION);
      } else if (key === keyCodes.KEY_LEFT) {
        newSelectedIndex = getNextSelectableIndex(PREVIOUS_TAB_DIRECTION);
      } else if (key === keyCodes.KEY_END || key === keyCodes.KEY_PAGE_DOWN) {
        newSelectedIndex = getNextSelectableIndex(PREVIOUS_TAB_DIRECTION, items.length);
      } else if (key === keyCodes.KEY_HOME || key === keyCodes.KEY_PAGE_UP) {
        newSelectedIndex = getNextSelectableIndex(NEXT_TAB_DIRECTION, -1);
      }
    }

    if (newSelectedIndex !== currentSelectedIndex) {
      updateSelectedIndex(newSelectedIndex);
      event.preventDefault();
    }
  }

  function getContentId(index) {
    return `${tabsId}-tab-panel-${index}`;
  }

  function getInitialSelectedIndex() {
    return selectedIndex ?? defaultSelectedIndex ?? 0;
  }

  function getNextSelectableIndex(direction, startingTabIndex = currentSelectedIndex) {
    const tabsCount = items.length;
    let newSelectedIndex = (startingTabIndex + direction + tabsCount) % tabsCount;

    if (items[newSelectedIndex]?.disabled) {
      newSelectedIndex = getNextSelectableIndex(direction + direction);
    }

    return newSelectedIndex;
  }

  function getTabId(index) {
    return `${tabsId}-tab-${index}`;
  }

  function updateSelectedIndex(index) {
    if (index !== currentSelectedIndex) {
      setAnimate(true);
    }
    setCurrentSelectedIndex(index);
    setFocusedTabIndex(index);
    onChange?.(index);
  }

  function ensureButtonIsFullyVisible() {
    const index = focusedTabIndex !== -1 ? focusedTabIndex : currentSelectedIndex;
    let targetScrollLeft = 0;

    if (index !== -1 && document.getElementById(getTabId(index)) && tabsRef.current) {
      const button = document.getElementById(getTabId(index));
      const container = tabsRef.current;
      const buttonLeft = button.offsetLeft;
      const buttonRight = buttonLeft + button.offsetWidth;
      const containerWidth = container.offsetWidth;
      const currentScrollLeft = Math.abs(scrollPosition);

      targetScrollLeft = currentScrollLeft;

      if (buttonLeft < currentScrollLeft) {
        targetScrollLeft = Math.max(0, buttonLeft);
      } else if (buttonRight > currentScrollLeft + containerWidth) {
        targetScrollLeft = buttonRight - containerWidth;
      }
    }

    return -targetScrollLeft;
  }

  function getWidthStyle() {
    return { width: `${100 / (items?.length || 1)}%` };
  }
};

Tabs.displayName = "Tabs";

Tabs.propTypes = {
  /** Indicator to enabled/disable tab panel content transitions. */
  animateContent: PropTypes.bool,

  /** Indicator to enabled/disable tab panel height transitions. */
  animateHeight: PropTypes.bool,

  /** Indicator to enabled/disable Tabs' initial render. */
  animateInitialRender: PropTypes.bool,

  /** String token provided to TransitionBlock. If none provided, uses selected index changes to trigger animation. */
  animationToken: PropTypes.string,

  /** aria-label text to provide accessibility description for next page button. */
  "aria-label-next": PropTypes.string.isRequired,

  /** aria-label text to provide accessibility description for previous page button. */
  "aria-label-previous": PropTypes.string.isRequired,

  /**
   * Enable/disable ability to programmatically set fullWidth if all content will be viewable without
   * scrolling.
   */
  autoFit: PropTypes.bool,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /** Additional classes for style the tabpanel content. */
  contentClassName: PropTypes.string,

  /**
   * Initial value for selected tab when not "controlled". selectedIndex should not be specified
   * when defaultSelectedIndex is given.
   */
  defaultSelectedIndex: PropTypes.number,

  /**
   * Global disabled state for all tabs, and will continue to display currently active tab.
   * Useful for loading states.
   */
  disabled: PropTypes.bool,

  /** Setting this prop will set all buttons to the same width and spread with of its container. */
  fullWidth: PropTypes.bool,

  /** Content that will be rendered. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      "aria-label": PropTypes.string,
      content: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
      disabled: PropTypes.bool,
      key: PropTypes.string,
      label: PropTypes.node,
    })
  ).isRequired,

  /** Event handler to be informed when a new tab is selected. */
  onChange: PropTypes.func,

  /** Event handler to be informed when a key is pressed. */
  onKeyDown: PropTypes.func,

  /** Function called when a tab height animation completes. */
  onTransitionEnd: PropTypes.func,

  /** Predefined padding size to replace default padding. */
  padding: PropTypes.oneOf(["none", "normal", "small"]),

  /** Padding for content area. */
  paddingContent: PropTypes.oneOf(["none", "normal"]),

  /**
   * Index of selected tab when the caller controls which tab is selected. When selectIndex is not
   * given, Tabs will handle the selection of tabs internally.
   */
  selectedIndex: PropTypes.number,

  /** Whether to render with a solid background behind the tab contents. */
  showBackground: PropTypes.bool,
};
